/**
 * API for data fetching.
 */

import FetchApi from './fetchApi';

class internalApi {
	/**
	 * Constructor.
	 */
	constructor() {
		this.fetchApi = new FetchApi( this );
		this.nonce = null;
	}

	/**
	 * Fetch the product.
	 *
	 * @param {number} productId
	 */
	async fetchProduct( productId ) {
		const productData = await this.fetchApi.get( `${ QalaBuy.quickviewApi }/product/${ productId }` ).then( result => {
			if ( result instanceof Error || result.status === 204 ) {
				return result;
			}
			return result.json();
		} );
		return productData;
	}

	/**
	 * Add a product to the cart.
	 *
	 * @param {number} productId
	 * @param {Array}  variationData
	 * @param {number} quantity
	 */
	async addToCart( productId, variationData = null, quantity = 1 ) {
		this.nonce = this.getNonce();

		if ( ! this.nonce ) {
			// If we could not find a nonce, we will try to generate one instead.
			await this.generateNonce();
		}

		const headers = {
			'Content-Type': 'application/json',
			Nonce: this.nonce,
		};

		const body = {
			id: productId,
			quantity,
		};

		// add variation data if provided.
		if ( variationData ) {
			body.variation = variationData;
		}

		const cart = await this.fetchApi.post( `${ QalaBuy.storeApi }/cart/add-item`, headers, body ).then( result => {
			if ( result instanceof Error ) {
				return result;
			}
			return result.json();
		} );

		return cart;
	}

	/**
	 * Check if there's a valid nonce.
	 * If not, attempt to fetch one for us.
	 *
	 * TODO: This should perhaps be used in a store rather than localStorage. But it seemed overkill to pull in a react store for this.
	 * potentially we could use the WP store functionality but there was no time to do this at the time of writing this.
	 * https://unfoldingneurons.com/2020/wordpress-data-how-to-register-a-custom-store
	 */
	getNonce() {
		const nonce = this.getStorageWithExpiry( 'quickviewStore' );

		if ( ! nonce ) {
			// Get the nonce from our localized script data.
			const nonceTimestamp = parseInt( QalaBuy.storeApiNonceTimestamp, 10 );
			if ( Date.now() < nonceTimestamp ) {
				return QalaBuy.storeApiNonce;
			}
		}

		return nonce;
	}

	/**
	 * Set the nonce in the storage.
	 *
	 * @param {string} nonce
	 */
	setNonce = nonce => {
		// Get 20h in milliseconds. Nonces in WP expire after 24h.
		const nonceTTL = 1000 * 60 * 60 * 20;
		this.setStorageWithExpiry( 'quickviewStore', nonce, nonceTTL );
	};

	/**
	 * Fetch a valid nonce by fetching the cart endpoint and using the headers in the response.
	 */
	async generateNonce() {
		const nonceData = await this.fetchApi.get( `${ QalaBuy.storeApi }/cart` ).then( result => {
			if ( result ) {
				return {
					nonce: result.headers.get( 'Nonce' ),
					nonceTimestamp: result.headers.get( 'Nonce-Timestamp' ),
				};
			}
			return null;
		} );

		if ( nonceData?.nonce ) {
			this.nonce = nonceData.nonce;
			this.setNonce( nonceData.nonce, nonceData.nonceTimestamp );
		}

		return nonceData?.nonce;
	}

	/**
	 * Sets a localstorage item with an expiry.
	 *
	 * @param {string} key
	 * @param {string} value
	 * @param {number} ttl
	 */
	setStorageWithExpiry = ( key, value, ttl ) => {
		const now = new Date();
		const timestamp = now.getTime() + ttl;

		// `item` is an object which contains the original value
		// as well as the time when it's supposed to expire
		const item = {
			value,
			expiry: timestamp,
		};

		window.localStorage.setItem( key, JSON.stringify( item ) );
	};

	/**
	 * Get a localstorage item with an expiry.
	 * If the item is expired, it will be removed and the function returns null.
	 *
	 * @param {string} key
	 */
	getStorageWithExpiry = key => {
		const itemStr = window.localStorage.getItem( key );
		// if the item doesn't exist, return null
		if ( ! itemStr ) {
			return null;
		}
		const item = JSON.parse( itemStr );
		// compare the expiry time of the item with the current time
		if ( Date.now() > parseInt( item.expiry, 10 ) ) {
			// If the item is expired, delete the item from storage
			// and return null
			localStorage.removeItem( key );
			return null;
		}
		return item.value;
	};

	/**
	 * Save nonce headers to our storage.
	 *
	 * @param {Object} headers
	 */
	saveNonceHeaders = headers => {
		const headerNonce = headers.get( 'Nonce' );
		this.nonce = headerNonce;
		if ( headerNonce ) {
			this.setNonce( headerNonce );
		}
	};
}

export default internalApi;
